import { useState, useRef, useCallback } from 'react'
import './BlockRenderer.css'
import './template.css'
// AUTO-SYNC:IMPORTS:BEGIN
import { BannerView } from '@shared/units/BannerView'
import { GalleryMosaicView } from '@shared/units/GalleryMosaicView'
import { GallerySliderView } from '@shared/units/GallerySliderView'
import { GalleryView } from '@shared/units/GalleryView'
import { ImageView } from '@shared/units/ImageView'
import { ReviewView } from '@shared/units/ReviewView'
import { SliderView } from '@shared/units/SliderView'
import { TextView } from '@shared/units/TextView'
import { TitleView } from '@shared/units/TitleView'
import { VideoView } from '@shared/units/VideoView'
// AUTO-SYNC:IMPORTS:END

// AUTO-SYNC:ASSETS:BEGIN
import bgStyle1 from '@shared/assets/bg_style1_x1.png'
import bgStyle2 from '@shared/assets/bg_style2_x1.png'
import titleSupplyStyle1 from '@shared/assets/title_supply_style1_x2.png'
import titleProtectStyle1 from '@shared/assets/title_protect_style1_x2.png'
import titleSupplyStyle2 from '@shared/assets/title_supply_style2_x2.png'
import titleProtectStyle2 from '@shared/assets/title_protect_style2_x2.png'
import Tpl2Subtitle1Image from '@shared/assets/2subtitle1_Group_34511_x2.png'
import Tpl2Subtitle2Image from '@shared/assets/2subtitle2_Group_34510_x2.png'
import templateBannerStyle1 from '@shared/assets/template_banner_style1_x2.png'
import T4Subtitle1Image from '@shared/assets/template4/subtitle1.png'
import T4Subtitle2Image from '@shared/assets/template4/subtitle2.png'
import bgStyle4 from '@shared/assets/template4/bg.png'
// AUTO-SYNC:ASSETS:END

// ── Block type → View component map ──────────────────────────────────────────
// AUTO-SYNC:BLOCK_MAP:BEGIN
const BLOCK_MAP = {
  banner: BannerView,
  'gallery-mosaic': GalleryMosaicView,
  'gallery-slider': GallerySliderView,
  gallery: GalleryView,
  image: ImageView,
  review: ReviewView,
  slider: SliderView,
  text: TextView,
  title: TitleView,
  video: VideoView,
}
// AUTO-SYNC:BLOCK_MAP:END

// ── Subtitle image maps（跟 admin NewsPreview 相同邏輯）──────────────────────
const TEMPLATE1_SUBTITLE = {
  'uuid-subtitle-1': titleSupplyStyle1,
  'uuid-subtitle-2': titleProtectStyle1,
}
const TEMPLATE3_SUBTITLE = {
  'uuid-subtitle-1': titleSupplyStyle2,
  'uuid-subtitle-2': titleProtectStyle2,
}
const TEMPLATE2_SUBTITLE = {
  'uuid-subtitle-1': Tpl2Subtitle1Image,
  'uuid-subtitle-2': Tpl2Subtitle2Image,
}
const TEMPLATE4_SUBTITLE = {
  'uuid-subtitle-1': T4Subtitle1Image,
  'uuid-subtitle-2': T4Subtitle2Image,
}

// ── Gallery group detection（跟 admin NewsPreview 相同邏輯）─────────────────
function isGalleryImage(block) {
  return block.type === 'image' && (block.layout?.w === 3 || block.layout?.w === 4)
}

// Template 5 固定配對
const T5_PAIR_A = { imageId: 'uuid-image-2', reviewId: 'uuid-review-2', imageLeft: true }
const T5_PAIR_B = { reviewId: 'uuid-review-3', imageId: 'uuid-image-3', imageLeft: false }
const T5_PAIR_C = { imageId: 'uuid-image-4', reviewId: 'uuid-review-4', imageLeft: true }
const T5_PAIR_D = { reviewId: 'uuid-review-5', imageId: 'uuid-image-5', imageLeft: false }
const T5_PAIR_E = { imageId: 'uuid-image-6', reviewId: 'uuid-review-6', imageLeft: true }
const T5_GREEN_GROUP_IDS = ['uuid-subtitle-2', 'uuid-video-1', 'uuid-video-2']

function groupBlocks(blocks, templateId) {
  const result = []
  let galleryGroup = []
  const isTemplate1 = templateId === 1
  const isTemplate3 = templateId === 3
  const isTemplate5 = templateId === 5

  // Template 1 & 3: pair uuid-review-N + uuid-image-N
  let reviewPairIdx = 0
  const pairedImageIds = new Set()
  if (isTemplate1 || isTemplate3) {
    blocks.forEach((block, index) => {
      if (block.type === 'review' && block.id?.startsWith('uuid-review-')) {
        const num = block.id.replace('uuid-review-', '')
        const nextBlock = blocks[index + 1]
        if (nextBlock && nextBlock.id === `uuid-image-${num}`) {
          pairedImageIds.add(nextBlock.id)
        }
      }
    })
  }

  // Template 5: mark consumed block ids
  if (isTemplate5) {
    pairedImageIds.add('uuid-review-2')
    pairedImageIds.add('uuid-image-3')
    pairedImageIds.add('uuid-review-4')
    pairedImageIds.add('uuid-image-5')
    pairedImageIds.add('uuid-review-6')
    pairedImageIds.add('uuid-title-en-1')
  }

  const t5GreenGroupSet = new Set(isTemplate5 ? T5_GREEN_GROUP_IDS : [])
  let t5GreenGroupInserted = false

  blocks.forEach((block, index) => {
    // Template 5 green group
    if (isTemplate5 && t5GreenGroupSet.has(block.id)) {
      if (!t5GreenGroupInserted) {
        const groupBlocks = T5_GREEN_GROUP_IDS
          .map(id => blocks.find(b => b.id === id))
          .filter(Boolean)
        result.push({ type: 't5-green-group', blocks: groupBlocks })
        t5GreenGroupInserted = true
      }
      return
    }

    if (isGalleryImage(block)) {
      galleryGroup.push(block)
      const next = blocks[index + 1]
      if (!next || !isGalleryImage(next)) {
        result.push({ type: 'gallery-group', blocks: [...galleryGroup] })
        galleryGroup = []
      }
    } else if ((isTemplate1 || isTemplate3) && block.type === 'review' && block.id?.startsWith('uuid-review-')) {
      const num = block.id.replace('uuid-review-', '')
      const nextBlock = blocks[index + 1]
      if (nextBlock && nextBlock.id === `uuid-image-${num}`) {
        result.push({ type: 'review-pair', review: block, image: nextBlock, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (isTemplate5 && block.id === T5_PAIR_A.imageId) {
      const reviewBlock = blocks.find(b => b.id === T5_PAIR_A.reviewId)
      if (reviewBlock) {
        result.push({ type: 't5-review-pair', image: block, review: reviewBlock, imageLeft: true, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (isTemplate5 && block.id === T5_PAIR_B.reviewId) {
      const imageBlock = blocks.find(b => b.id === T5_PAIR_B.imageId)
      if (imageBlock) {
        result.push({ type: 't5-review-pair', image: imageBlock, review: block, imageLeft: false, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (isTemplate5 && block.id === T5_PAIR_C.imageId) {
      const reviewBlock = blocks.find(b => b.id === T5_PAIR_C.reviewId)
      if (reviewBlock) {
        result.push({ type: 't5-review-pair', image: block, review: reviewBlock, imageLeft: true, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (isTemplate5 && block.id === T5_PAIR_D.reviewId) {
      const imageBlock = blocks.find(b => b.id === T5_PAIR_D.imageId)
      if (imageBlock) {
        result.push({ type: 't5-review-pair', image: imageBlock, review: block, imageLeft: false, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (isTemplate5 && block.id === T5_PAIR_E.imageId) {
      const reviewBlock = blocks.find(b => b.id === T5_PAIR_E.reviewId)
      if (reviewBlock) {
        result.push({ type: 't5-review-pair', image: block, review: reviewBlock, imageLeft: true, pairIdx: reviewPairIdx++ })
      } else {
        result.push(block)
      }
    } else if (pairedImageIds.has(block.id)) {
      // skip — already consumed
    } else {
      result.push(block)
    }
  })
  return result
}

// ── BlockWrapper — 處理外層 class + 寬度 ─────────────────────────────────────
function BlockWrapper({ block, templateId, reviewImageIdx, reviewIdx, t4ReviewIdx, isTemplate1, isTemplate2, isTemplate3, isTemplate4, isTemplate5, isTemplate6, blockIdx, allBlocks }) {
  const w = block.layout?.w ?? 12
  const x = block.layout?.x ?? 0
  const is3Subtitle = isTemplate3 && (block.id === 'uuid-subtitle-1' || block.id === 'uuid-subtitle-2')
  const is4Subtitle = isTemplate4 && (block.id === 'uuid-subtitle-1' || block.id === 'uuid-subtitle-2')
  const is5Subtitle = isTemplate5 && (block.id === 'uuid-subtitle-2' || block.id === 'uuid-subtitle-3')
  const desktopWidth = (w === 12 || is3Subtitle || is4Subtitle || is5Subtitle) ? '100%' : `calc(${(w / 12) * 100}% - 1rem)`
  const desktopMargin = (x && !is3Subtitle && !is4Subtitle && !is5Subtitle) ? `${(x / 12) * 100}%` : '0px'

  const Component = BLOCK_MAP[block.type]
  if (!Component) return null

  // ── Subtitle 特殊圖片（不走一般 Component）────────────────────────────────
  const subtitleImg = isTemplate3
    ? TEMPLATE3_SUBTITLE[block.id]
    : isTemplate1
      ? TEMPLATE1_SUBTITLE[block.id]
      : undefined

  if (subtitleImg) {
    const isRight = block.id === 'uuid-subtitle-1'
    const isSubtitle1 = block.id === 'uuid-subtitle-1'
    const wrapClass = isSubtitle1 ? 'subtitle-1-wrap' : 'subtitle-2-wrap'
    const imgClass = isSubtitle1 ? 'subtitle-1' : 'subtitle-2'
    return (
      <div className={`w-full basis-full tpl1-subtitle-wrap ${wrapClass}`}>
        <img src={subtitleImg} alt="" className={`tpl1-subtitle-img ${imgClass}`} />
      </div>
    )
  }

  const tpl2SubtitleImg = isTemplate2 ? TEMPLATE2_SUBTITLE[block.id] : undefined
  if (tpl2SubtitleImg) {
    const is1 = block.id === 'uuid-subtitle-1'
    return (
      <div className={`w-full basis-full ${is1 ? 't2-subtitle1-div desktop' : 't2-subtitle2-div desktop'}`}>
        <img src={tpl2SubtitleImg} alt="" className={is1 ? 't2-subtitle1-image desktop' : 't2-subtitle2-image desktop'} />
      </div>
    )
  }

  const tpl4SubtitleImg = isTemplate4 ? TEMPLATE4_SUBTITLE[block.id] : undefined
  if (tpl4SubtitleImg) {
    const is1 = block.id === 'uuid-subtitle-1'
    return (
      <div className={`w-full basis-full ${is1 ? 't4-subtitle1-div desktop' : 't4-subtitle2-div desktop'}`}>
        <img src={tpl4SubtitleImg} alt="" className={is1 ? 't4-subtitle1-image desktop' : 't4-subtitle2-image desktop'} />
      </div>
    )
  }

  // ── Template 5 title: 紫色塊 + 主標 + en標 + hardcoded 副標 ──────────────
  if (isTemplate5 && block.type === 'title' && block.id === 'uuid-title-1') {
    const titleValue = block.data?.value || ''
    const enBlock = allBlocks?.find(b => b.id === 'uuid-title-en-1')
    const enValue = enBlock?.data?.value || ''
    return (
      <div className="basis-full w-full tpl5-title-wrap">
        <div className="tpl5-title-box">
          <div className="tpl5-title-main" dangerouslySetInnerHTML={{ __html: titleValue }} />
          {enValue && <div className="tpl5-title-en" dangerouslySetInnerHTML={{ __html: enValue }} />}
          <p className="tpl5-title-sub">文星點燈法會</p>
          <p className="tpl5-title-sub-en">Lighting the Lamp of Wisdom Ceremony</p>
        </div>
      </div>
    )
  }

  // ── Template 5 subtitle: pure CSS, no image asset ─────────────────────────
  if (is5Subtitle) {
    const subtitleNum = block.id.replace('uuid-subtitle-', '')
    return (
      <div className={`w-full basis-full tpl5-subtitle tpl5-subtitle-${subtitleNum}`}>
        {subtitleNum === '1' && (
          <div className="tpl5-subtitle-inner">
            <div className="tpl5-subtitle-tag"><span>傳</span><span>燈</span></div>
            <div className="tpl5-subtitle-text">
              <p className="tpl5-subtitle-zh">即時轉播</p>
              <p className="tpl5-subtitle-en">Live Broadcast</p>
            </div>
          </div>
        )}
        {subtitleNum === '2' && (
          <div className="tpl5-subtitle-inner">
            <div className="tpl5-subtitle-tag tpl5-subtitle-tag--2"><span>供</span><span>燈</span></div>
            <div className="tpl5-subtitle-text">
              <p className="tpl5-subtitle-zh">影音報導</p>
              <p className="tpl5-subtitle-en">Video Report</p>
            </div>
          </div>
        )}
        {subtitleNum === '3' && (
          <div className="tpl5-subtitle-inner tpl5-subtitle-inner--3">
            <div className="tpl5-subtitle-circle" />
            <div className="tpl5-subtitle-tag"><span>護</span><span>燈</span></div>
            <div className="tpl5-subtitle-text">
              <p className="tpl5-subtitle-zh">學員心得</p>
              <p className="tpl5-subtitle-en">Testimonials</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Banner: 全寬，抵消 article 的 px-4 ───────────────────────────────────
  if (block.type === 'banner') {
    return (
      <div
        className="relative -mx-4 shrink-0"
        style={{
          flexBasis: 'calc(100% + 2rem)',
          width: 'calc(100% + 2rem)',
          ...(blockIdx === 0 ? { marginTop: '-2rem' } : {})
        }}
      >
        <BannerView
          desktop={block.data?.desktop}
          mobile={block.data?.mobile}
          url={block.data?.url}
        />
        {isTemplate2 && (
          <div className="pointer-events-none t2-banner-gradient" />
        )}
      </div>
    )
  }

  // ── Template-specific block flags ─────────────────────────────────────────
  const isReviewImage = (isTemplate1 || isTemplate3) && block.type === 'image' && block.id?.startsWith('uuid-image-')
  const is1Or3ReviewImage = isReviewImage
  const is1Video = isTemplate1 && block.type === 'video' && block.id === 'uuid-video-1'
  const is2Video = isTemplate2 && block.type === 'video' && block.id === 'uuid-video-1'
  const is3Video = isTemplate3 && block.type === 'video' && block.id === 'uuid-video-1'
  const is4Video = isTemplate4 && block.type === 'video' && block.id === 'uuid-video-1'
  const is5Video = isTemplate5 && block.type === 'video'
  const is4Review = isTemplate4 && block.type === 'review'
  const is3Review = isTemplate3 && block.type === 'review'
  const is3Content = isTemplate3 && block.id === 'uuid-content-1'
  const is5Image4 = isTemplate5 && block.id === 'uuid-image-4'
  const is5Review1 = isTemplate5 && block.id === 'uuid-review-1'

  const imagePosition = is1Or3ReviewImage
    ? (isTemplate3
      ? (reviewImageIdx % 2 === 0 ? 'right' : 'left')
      : (reviewImageIdx % 2 === 0 ? 'left' : 'right'))
    : undefined

  // Template 4 review imagePosition: left/right alternation
  const t4ReviewImagePos = is4Review
    ? (t4ReviewIdx % 2 === 0 ? 'left' : 'right')
    : undefined

  // Review imagePosition: template 1/3 or template 4
  const reviewImagePosition = (isTemplate1 || isTemplate3)
    ? (isTemplate3 ? (reviewIdx % 2 === 0 ? 'left' : 'right') : (reviewIdx % 2 === 0 ? 'right' : 'left'))
    : is4Review ? t4ReviewImagePos
    : undefined

  const props = {
    ...(block.type === 'video' ? { url: block.data?.url, thumbnail: block.data?.thumbnail } : {}),
    ...(block.type === 'image' ? { url: block.data?.url, caption: block.data?.caption, gridW: w, imagePosition: isTemplate3 ? imagePosition : undefined } : {}),
    ...(block.type === 'title' ? { value: block.data?.value, plainText: isTemplate6 } : {}),
    ...(block.type === 'text' ? { value: block.data?.value, fontSize: block.data?.fontSize } : {}),
    ...(block.type === 'review' ? { name: block.data?.name, title: block.data?.title, content: block.data?.content, templateId, useTemplate3Style: isTemplate3, imagePosition: reviewImagePosition } : {}),
    ...(block.type === 'slider' ? { slides: block.data?.slides ?? [] } : {}),
    ...(block.type === 'gallery' ? { data: block.data, config: block.config } : {}),
  }

  // Template 1 uuid-image-N: 4/5 寬度左右交替（Template 3 由 ImageView 內部 posClass 處理）
  const content = is1Or3ReviewImage && isTemplate1 ? (
    <div className={reviewImageIdx % 2 === 0 ? 'w-4/5 ml-0 mr-auto' : 'w-4/5 ml-auto mr-0'}>
      <Component {...props} />
    </div>
  ) : (
    <Component {...props} />
  )

  const wrapperClasses = [
    // 寬度：subtitleBlock 全寬，video 由 CSS class 控制，uuid-image-N 全寬
    is3Subtitle || is4Subtitle || is5Subtitle || is1Or3ReviewImage || is5Review1 ? 'w-full basis-full' : 'block-width-wrapper w-full',
    // Template-specific wrapper classes
    is3Subtitle && 'tpl3-subtitle',
    is4Subtitle && 't4-subtitle',
    is5Subtitle && `tpl5-subtitle tpl5-subtitle-${block.id.replace('uuid-subtitle-', '')}`,
    is3Review && 'tpl3-review',
    is4Review && 't4-review',
    is3Content && 'tpl3-content-1',
    is1Video && 'tpl1-video',
    is3Video && 'tpl1-video',
    is2Video && 'news-template-2-video',
    is4Video && 't4-video',
    is5Video && 'tpl5-video',
    is5Image4 && 'tpl5-image-4',
    isTemplate5 && block.id === 'uuid-image-1' && 'tpl5-image-1',
    is1Or3ReviewImage && isTemplate1 && 'mb-8',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={wrapperClasses}
      style={(!is3Subtitle && !is4Subtitle && !is5Subtitle && !is1Or3ReviewImage && !is1Video && !is2Video && !is3Video && !is4Video && !is5Video)
        ? { '--block-w': desktopWidth, '--block-m': desktopMargin }
        : undefined}
    >
      {content}
    </div>
  )
}

// ── 判斷 block 是否為空（配合 removable 使用）───────────────────────────────
function isBlockEmpty(block) {
  const d = block.data ?? {}
  switch (block.type) {
    case 'review': return !d.content && !d.name
    case 'review-image': return !d.content && !d.name
    case 'image': return !d.url
    case 'video': return !d.url
    case 'banner': return !d.desktop?.url && !d.mobile?.url && !d.url
    case 'text': return !d.value
    case 'slider': return !d.slides?.length
    default: return false
  }
}

// ── BlockRenderer — 主入口 ───────────────────────────────────────────────────
export function BlockRenderer({ blocks, templateId }) {
  if (!blocks?.length) return null

  const isTemplate1 = templateId === 1
  const isTemplate2 = templateId === 2
  const isTemplate3 = templateId === 3
  const isTemplate4 = templateId === 4
  const isTemplate5 = templateId === 5
  const isTemplate6 = templateId === 6

  // uuid-image-N 奇偶計數
  const reviewImageIndexMap = new Map()
  // uuid-review-N 奇偶計數（決定 review 文字左右位置）
  const reviewIndexMap = new Map()
  const t4ReviewIndexMap = new Map()
  if (isTemplate1 || isTemplate3) {
    let ri = 0
    let rv = 0
    blocks.forEach(b => {
      if (b.type === 'image' && b.id?.startsWith('uuid-image-')) reviewImageIndexMap.set(b.id, ri++)
      if (b.type === 'review' && b.id?.startsWith('uuid-review-')) reviewIndexMap.set(b.id, rv++)
    })
  }
  if (isTemplate4) {
    let ri = 0
    blocks.forEach(b => { if (b.type === 'review') t4ReviewIndexMap.set(b.id, ri++) })
  }


  const grouped = groupBlocks(blocks, templateId)

  const bgStyle = isTemplate1
    ? { backgroundImage: `url(${bgStyle1})`, backgroundRepeat: 'repeat-y', backgroundSize: '100% auto' }
    : isTemplate3
      ? { backgroundImage: `url(${bgStyle2})`, backgroundRepeat: 'repeat-y', backgroundSize: '100% auto' }
      : isTemplate4
        ? { backgroundImage: `url(${bgStyle4})`, backgroundRepeat: 'repeat-y', backgroundSize: '100% auto' }
        : undefined

  return (
    <div className="tpl-container">
      <article
        className={[
          'max-w-screen-lg mx-auto py-8 px-4',
          isTemplate1 && 'tpl1',
          isTemplate2 && 'news-template-2',
          isTemplate3 && 'tpl3',
          isTemplate4 && 'tpl4',
          isTemplate5 && 'tpl5',
        ].filter(Boolean).join(' ')}
        style={bgStyle}
      >
        <div className="flex flex-wrap gap-4">
          {grouped.map((item, idx) => {
            // Gallery group
            if (item.type === 'gallery-group') {
              const slides = item.blocks
                .filter(b => b.data?.url)
                .map(b => ({ id: b.id, url: b.data.url, caption: b.data.caption }))
              if (slides.length === 0) return null
              return (
                <div key={`gallery-${idx}`} className="w-full">
                  <GallerySliderView slides={slides} />
                </div>
              )
            }

            // Review pair (template 1 & 3): review + image in one wrapper
            if (item.type === 'review-pair') {
              const { review, image, pairIdx } = item
              const isEven = pairIdx % 2 === 0
              const reviewPos = isTemplate1
                ? (isEven ? 'right' : 'left')
                : (isEven ? 'left' : 'right')
              const imagePos = reviewPos === 'left' ? 'right' : 'left'
              const reviewHidden = review.removable && isBlockEmpty(review)
              const imageHidden = image.removable && isBlockEmpty(image)
              if (reviewHidden && imageHidden) return null
              const imgIdx = reviewImageIndexMap.get(image.id) ?? 0
              return (
                <div key={`review-pair-${pairIdx}`} className={['basis-full w-full', isTemplate1 && 'mb-8'].filter(Boolean).join(' ')}>
                  {!reviewHidden && (
                    <ReviewView
                      name={review.data?.name}
                      content={review.data?.content}
                      templateId={templateId}
                      useTemplate3Style={isTemplate3}
                      imagePosition={reviewPos}
                    />
                  )}
                  {!imageHidden && (
                    <BlockWrapper
                      block={image}
                      templateId={templateId}
                      reviewImageIdx={imgIdx}
                      reviewIdx={0}
                      t4ReviewIdx={0}
                      isTemplate1={isTemplate1}
                      isTemplate2={false}
                      isTemplate3={isTemplate3}
                      isTemplate4={false}
                      isTemplate5={false}
                      blockIdx={idx}
                    />
                  )}
                </div>
              )
            }

            // Template 5: green background group
            if (item.type === 't5-green-group') {
              const T5_SUBTITLE_IDS = new Set(['uuid-subtitle-2'])
              return (
                <div key={`t5-green-group-${idx}`} className="basis-full w-full tpl5-green-group-wrap">
                  <div className="tpl5-green-group">
                    {item.blocks.map(block => {
                      if (block.removable && isBlockEmpty(block)) return null
                      const isSubtitle = T5_SUBTITLE_IDS.has(block.id)
                      const subtitleNum = isSubtitle ? block.id.replace('uuid-subtitle-', '') : null
                      return (
                        <div key={block.id} className={[
                          isSubtitle && `tpl5-subtitle tpl5-subtitle-${subtitleNum}`,
                          block.type === 'video' && `tpl5-video-wrap tpl5-video-wrap-${block.id.replace('uuid-video-', '')}`
                        ].filter(Boolean).join(' ')}>
                          {isSubtitle ? (
                            <div className="tpl5-subtitle-inner">
                              <div className="tpl5-subtitle-tag">
                                <><span>供</span><span>燈</span></>
                              </div>
                              <div className="tpl5-subtitle-text">
                                <p className="tpl5-subtitle-zh">影音報導</p>
                                <p className="tpl5-subtitle-en">Video Report</p>
                              </div>
                            </div>
                          ) : block.data?.url ? (
                            <VideoView url={block.data?.url} thumbnail={block.data?.thumbnail} />
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                  <div className="tpl5-orange-bar" aria-hidden="true" />
                </div>
              )
            }

            // Template 5: image + review pair（左右交替）
            if (item.type === 't5-review-pair') {
              const { image, review, imageLeft, pairIdx } = item
              const imageHidden = image.removable && isBlockEmpty(image)
              const reviewHidden = review.removable && isBlockEmpty(review)
              if (imageHidden && reviewHidden) return null
              return (
                <div key={`t5-review-pair-${pairIdx}`} className={['basis-full w-full flex gap-1 tpl5-review-pair-wrap', !imageLeft && 'tpl5-review-pair-wrap--reverse'].filter(Boolean).join(' ')}>
                  <div className="flex-1 min-w-0 tpl5-review-pair-img">
                    {!imageHidden && <ImageView url={image.data?.url} caption={image.data?.caption} />}
                  </div>
                  <div className="flex-1 min-w-0 tpl5-review-pair-review">
                    {!reviewHidden && (
                      <ReviewView
                        name={review.data?.name}
                        title={review.data?.title}
                        content={review.data?.content}
                        templateId={templateId}
                      />
                    )}
                  </div>
                </div>
              )
            }

            const block = item
            if (!block.id) return null

            // removable + 空 → 隱藏（同 admin NewsPreview 邏輯）
            if (block.removable && isBlockEmpty(block)) return null

            const reviewImageIdx = reviewImageIndexMap.get(block.id) ?? 0
            const reviewIdx = reviewIndexMap.get(block.id) ?? 0
            const t4ReviewIdx = t4ReviewIndexMap.get(block.id) ?? 0

            return (
              <BlockWrapper
                key={block.id}
                block={block}
                templateId={templateId}
                reviewImageIdx={reviewImageIdx}
                reviewIdx={reviewIdx}
                t4ReviewIdx={t4ReviewIdx}
                isTemplate1={isTemplate1}
                isTemplate2={isTemplate2}
                isTemplate3={isTemplate3}
                isTemplate4={isTemplate4}
                isTemplate5={isTemplate5}
                isTemplate6={isTemplate6}
                blockIdx={idx}
                allBlocks={blocks}
              />
            )
          })}
        </div>
      </article>
    </div>
  )
}
